import { Component, computed, ElementRef, HostListener, inject, OnDestroy, Signal, ViewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { HotkeyBinding, HotkeyService } from '@app/core/services/hotkey.service';
import { OverlayService } from '@app/core/services/overlay.service';

interface HotkeyGroup {
  group: string;
  bindings: HotkeyBinding[];
}

@Component({
  selector: 'app-hotkey-index',
  imports: [TranslatePipe],
  templateUrl: './hotkey-index.component.html',
  styleUrl: './hotkey-index.component.css',
})
export class HotkeyIndexComponent implements OnDestroy {
  public readonly hotkeyService = inject(HotkeyService);
  private readonly overlayService = inject(OverlayService);
  private readonly router = inject(Router);

  @ViewChild('panel') private readonly panel!: ElementRef<HTMLElement>;

  public isOpen: boolean = false;

  private readonly subscriptions = new Subscription();

  constructor() {
    // Close the overlay on any successful navigation so it doesn't linger
    // over the new page (e.g. after the user triggers a navigation hotkey).
    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          if (this.isOpen) this.close();
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Bindings grouped by their `group` field, excluding those without a label.
   * Page-level bindings are shown after global ones.
   */
  public readonly groups: Signal<HotkeyGroup[]> = computed(() => {
    const bindings = this.hotkeyService.bindings().filter(b => !!b.label);

    const map = new Map<string, HotkeyBinding[]>();
    for (const binding of bindings) {
      const group = binding.group ?? 'hotkeys.group.page';
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(binding);
    }

    // Sort so global navigation group comes first
    const order = ['hotkeys.group.navigation', 'hotkeys.group.page'];
    return Array.from(map.entries())
      .sort(([a], [b]) => {
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      })
      .map(([group, groupBindings]) => ({ group, bindings: groupBindings }));
  });

  public open(): void {
    this.isOpen = true;
    this.overlayService.show();
  }

  public close(): void {
    this.isOpen = false;
    this.overlayService.hide();
  }

  public toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.isOpen) this.close();
  }

  @HostListener('document:click', ['$event'])
  public onClickOutside(event: MouseEvent): void {
    if (this.isOpen && this.panel && !this.panel.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }
}
